import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import {
  AttachmentPreviewList,
  SendButton as DefaultSendButton,
  SimpleAttachmentSelector,
  TextareaComposer,
  TextareaComposerProps,
  useComponentContext,
  useMessageComposer,
  useMessageInputContext,
  WithDragAndDropUpload,
} from 'stream-chat-react';
import TurndownService from 'turndown';

import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import { CustomAttachmentPreview } from 'src/features/channel/custom-attachments/CustomAttachmentPreview';
import { AtMentionButton } from 'src/features/channel/custom-message-input/ActionButtons';
import { MessageRichTextInput } from 'src/features/channel/custom-message-input/MessageRichTextInput';
import {
  FileDropHintColor,
  useMessageInputDropZone,
} from 'src/features/channel/custom-message-input/useMessageInputDropZone';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { useOSDetector } from 'src/hooks/useOSDetector';

const TextareaComposerWrapper = (props: TextareaComposerProps) => {
  const { textareaRef } = useMessageInputContext();

  // there's a known bug in the autoresize text area library that GetStream uses
  // that causes it to grow to full height when it enters and leaves the viewport
  // https://github.com/Andarist/react-textarea-autosize/issues/393
  // this code detects when it enters the viewport and forces the component to rerender
  const [_, setForceRerender] = useState(false);
  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          setForceRerender((prev) => !prev);
        }
      },
      { root: null, rootMargin: '0px' },
    );

    if (textareaRef.current) {
      intersectionObserver.observe(textareaRef.current);
    }

    return () => intersectionObserver.disconnect();
  }, [textareaRef]);

  // we also need to force the textarea to rerender whenever we resize it
  // since the autoresize component under the hood only accounts for changes
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setForceRerender((prev) => !prev);
    });

    if (textareaRef.current) {
      resizeObserver.observe(textareaRef.current);
    }
  }, [textareaRef]);

  return <TextareaComposer {...props} />;
};

// created while referencing the default MessageInputFlat component
// https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageInput/MessageInputFlat.tsx
export const CustomMessageInput = () => {
  const EnableMessageEditorInput = useFeatureFlagEnabled(
    'message-editor-input',
  );
  const { handleSubmit, textareaRef } = useMessageInputContext();
  const { textComposer, attachmentManager } = useMessageComposer();
  const { SendButton = DefaultSendButton } = useComponentContext();
  const { isOSDeleteKeyPressed } = useOSDetector();
  const handleOnKeyDownCapture = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (isOSDeleteKeyPressed(event)) {
      event.stopPropagation();
    }
  };
  const { isSidebarTheme } = useMessagingTheme();
  const { isInternalChat } = useInternalChatContext();
  const [fileDropHintColor, setFileDropHintColor] =
    useState<FileDropHintColor | null>(null);
  const { messageInputRef } = useMessageInputDropZone({
    isInternalChat,
    attachmentManager,
    setFileDropHintColor,
  });

  const markdownConverter = useRef(new TurndownService());
  const convertHtmlToMarkdown = (htmlInput: string) => {
    return markdownConverter.current.turndown(htmlInput);
  };

  const [messageContent, setMessageContent] = useState(textComposer.text);

  const handleMessageContentChange = async (editor: Editor) => {
    const textContent = editor.getText();
    const htmlContent = editor.getHTML();
    // override the submit handler to use the new content
    await textComposer.handleChange({
      selection: {
        end: editor.state.selection.$to.pos,
        start: editor.state.selection.$from.pos,
      },
      text: convertHtmlToMarkdown(htmlContent),
    });
    setMessageContent(textContent);
  };

  const handleSendMessage = () => {
    handleSubmit();
    textComposer.setText('');
    setMessageContent('');
  };

  return (
    <WithDragAndDropUpload>
      <div
        ref={messageInputRef}
        className={clsx(
          isSidebarTheme ? 'px-4' : 'pl-4 pr-9',
          isInternalChat ? 'pb-3' : 'py-3',
        )}
      >
        <div
          className={clsx(
            'border border-solid border-non-hover-border active:border-black-heading focus-within:border-black-heading rounded-xl cursor-text',
            fileDropHintColor
              ? fileDropHintColor
              : isSidebarTheme
                ? 'bg-gray-100'
                : 'bg-white',
            EnableMessageEditorInput ? 'p-0' : 'px-3 py-2',
          )}
          role="textbox"
          tabIndex={0}
          onClick={() => textareaRef.current?.focus()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && event.target !== textareaRef.current) {
              event.preventDefault();
              textareaRef.current?.focus();
            }
          }}
        >
          {EnableMessageEditorInput ? (
            <div>
              <MessageRichTextInput
                handleSendMessage={handleSendMessage}
                content={messageContent}
                onContentChange={handleMessageContentChange}
              />
              {/* We intentionally moved the attachment preview
              below the message editor because it looks weird
              if it displays above the editor. Below looks better. */}
              <AttachmentPreviewList
                UnsupportedAttachmentPreview={CustomAttachmentPreview}
              />
            </div>
          ) : (
            <>
              <AttachmentPreviewList
                UnsupportedAttachmentPreview={CustomAttachmentPreview}
              />
              <TextareaComposerWrapper
                className="w-full border-none resize-none overflow-x-hidden p-0 mb-2 text-base placeholder-light-gray bg-inherit"
                containerClassName="relative" // this allows the suggestion list to be correctly positioned
                name="message input"
                onKeyDownCapture={handleOnKeyDownCapture}
              />
            </>
          )}

          <div
            className={clsx(
              'flex items-center justify-between',
              EnableMessageEditorInput ? 'px-3 py-2' : 'p-0',
            )}
          >
            <div className="flex items-center gap-1.5">
              {isInternalChat && (
                <AtMentionButton
                  onClick={() => {
                    textareaRef.current?.focus();
                    void textComposer.insertText({ text: '@' });
                  }}
                />
              )}
              <SimpleAttachmentSelector />
            </div>
            <SendButton sendMessage={handleSendMessage} />
          </div>
        </div>
      </div>
    </WithDragAndDropUpload>
  );
};
